import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import authClient from '../utils/authClient';
import apiClient from '../utils/apiClient';
import './Login.css';

const Login = () => {
  const [userName, setUserName] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [needsPasswordChange, setNeedsPasswordChange] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [userId, setUserId] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Check if user is already logged in
  useEffect(() => {
    if (authClient.isAuthenticated()) {
      // Redirect to the page they were trying to access or to the manage page
      const redirectTo = location.state?.from?.pathname || '/manage';
      navigate(redirectTo);
    }
  }, [navigate, location]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const response = await authClient.login(userName, password);
      
      // Check if user needs to change password
      if (response.user && response.user.needsPasswordChange) {
        setNeedsPasswordChange(true);
        setUserId(response.user.id);
        setIsLoading(false);
        return;
      }
      
      // Redirect to the page they were trying to access or to the manage page
      const redirectTo = location.state?.from?.pathname || '/manage';
      navigate(redirectTo);
    } catch (err) {
      console.error('Login error:', err);
      setError(err.response?.data?.message || 'Login failed. Please check your credentials.');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setError('');

    if (newPassword.length < 6) {
      setError('新密码必须至少6个字符');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('两次输入的密码不一致');
      return;
    }

    setIsLoading(true);

    try {
      await apiClient.post('/users/change-password', {
        userId,
        oldPassword: password,
        newPassword
      });

      // Re-login with new password
      await authClient.login(userName, newPassword);
      
      alert('密码修改成功！');
      
      // Redirect to the page they were trying to access or to the manage page
      const redirectTo = location.state?.from?.pathname || '/manage';
      navigate(redirectTo);
    } catch (err) {
      console.error('Password change error:', err);
      setError(err.response?.data?.message || '密码修改失败，请重试');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="login-page-container">
      <div className="login-page-form-container">
        {!needsPasswordChange ? (
          <>
            <h1>怀旧游戏博物馆管理员登陆</h1>
            <form className="login-page-form" onSubmit={handleSubmit}>
              {error && <div className="login-page-error-message">{error}</div>}
              
              <div className="login-page-form-group">
                <label htmlFor="userName">用户名</label>
                <input
                  type="text"
                  id="userName"
                  value={userName}
                  onChange={(e) => setUserName(e.target.value)}
                  required
                  placeholder="请输入用户名"
                />
              </div>
              
              <div className="login-page-form-group">
                <label htmlFor="password">密码</label>
                <input
                  type="password"
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  placeholder="请输入密码"
                />
              </div>
              
              <button 
                type="submit" 
                className="login-page-submit-button"
                disabled={isLoading}
              >
                {isLoading ? '登录中...' : '登录'}
              </button>
            </form>
          </>
        ) : (
          <>
            <h1>修改密码</h1>
            <p className="password-change-notice">
              您正在使用默认密码，为了账户安全，请设置新密码。
            </p>
            <form className="login-page-form" onSubmit={handlePasswordChange}>
              {error && <div className="login-page-error-message">{error}</div>}
              
              <div className="login-page-form-group">
                <label htmlFor="newPassword">新密码</label>
                <input
                  type="password"
                  id="newPassword"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  placeholder="请输入新密码（至少6个字符）"
                  minLength="6"
                />
              </div>
              
              <div className="login-page-form-group">
                <label htmlFor="confirmPassword">确认新密码</label>
                <input
                  type="password"
                  id="confirmPassword"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  placeholder="请再次输入新密码"
                  minLength="6"
                />
              </div>
              
              <button 
                type="submit" 
                className="login-page-submit-button"
                disabled={isLoading}
              >
                {isLoading ? '修改中...' : '确认修改'}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default Login;
